<script lang="ts" setup>
  import { computed } from 'vue';
  import { useFormContext } from '@othercommerce/foundation';
  import lodashGet from 'lodash.get';

  interface Props {
    id: string;
    name?: string | undefined;
    label?: string | undefined;
    required?: boolean;
    floating?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    required: false,
    floating: false,
  });

  const context = useFormContext();

  const touched = computed(() => {
    if (props.name && context && context.touched.value) {
      return lodashGet(context.touched.value, props.name, false);
    } else {
      return false;
    }
  });

  const errors = computed(() => {
    if (props.name && context && context.errors.value && !touched.value) {
      return lodashGet(context.errors.value, props.name, []);
    } else {
      return [];
    }
  });

  const classes = computed(() => {
    return {
      'control--required': props.required,
      'control--error': errors.value.length > 0,
      'control--floating': props.floating && props.label,
    };
  });

  const errorId = computed(() => {
    return errors.value.length > 0 ? props.name + '-error' : undefined;
  });
</script>
<template>
  <div class="control-wrapper" :class="classes">
    <label v-if="!floating && label" :for="id" class="label" v-html="label" />
    <div class="control-container" ref="templateControlReference">
      <slot :errorId="errorId" />
      <label v-if="floating && label" :for="id" class="label" v-html="label" />
    </div>
    <Transition name="fade">
      <div v-if="errors.length > 0" :id="name +'-error'" class="errors" role="alert" aria-live="assertive">
        <ul>
          <li v-for="error in errors" class="error">
            <span class="icon" role="presentation">
              <SfIconError aria-hidden="true" />
            </span>
            <span class="message" v-html="error" />
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>
<style lang="scss">
  :root {
    --sf-input-color: rgb(0 0 0);
    --sf-input-color-disabled: var(--disabled-foreground);
    --sf-input-font-family: var(--font-family-sans);
    --sf-input-font-size: 0.875rem;
    --sf-input-font-weight: var(--font-weight-regular);
    --sf-input-line-height: 1.25rem;

    --sf-input-floating-padding-top: 1.1875rem;
    --sf-input-floating-padding-bottom: 0.3125rem;
    --sf-input-floating-label-font-size: 1rem;

    --sf-input-padding-top: 0.75rem;
    --sf-input-padding-right: 1rem;
    --sf-input-padding-bottom: 0.75rem;
    --sf-input-padding-left: 1rem;

    --sf-input-number-btn-size: 2.75rem;
    --sf-input-number-btn-size-sm: 2rem;
    --sf-input-number-icon-size: 1.5rem;
    --sf-input-number-label-padding-left: 0;

    --sf-input-box-shadow: none;

    --sf-input-border-style: solid;
    --sf-input-border-bottom-width: 1px;
    --sf-input-border-top-width: 1px;
    --sf-input-border-right-width: 1px;
    --sf-input-border-left-width: 1px;
    --sf-input-border: rgb(10 10 10);
    --sf-input-border-active: var(--primary-color);
    --sf-input-border-focus: var(--primary-color);
    --sf-input-border-disabled: var(--disabled-background);
    --sf-input-border-radius: var(--border-radius);

    --sf-input-background: rgb(255 255 255);
    --sf-input-background-active: rgb(251 251 251);
    --sf-input-background-focus: rgb(240 240 240);
    --sf-input-background-disabled: var(--disabled-background);

    --sf-input-transition-property: background, border, color;
    --sf-input-transition-timing-func: ease-in;
    --sf-input-transition-duration: 150ms;

    --sf-input-label-font-size: 0.875rem;
    --sf-input-label-line-height: 0.875rem;
    --sf-input-label-color: var(--color-secondary, rgb(76, 76, 76));
    --sf-input-label-padding-top: 1rem;
    --sf-input-label-padding-bottom: 0.375rem;
    --sf-input-label-padding-left: var(--sf-input-padding-left);

    --sf-input-label-floating-opacity: 0.8;
    --sf-input-label-floating-font-size: 0.625rem;
    --sf-input-label-floating-top: 0.375rem;
    --sf-input-label-floating-padding-top: 0;

    --sf-input-label-required-color: #cc0000;

    --sf-input-error-padding-x: 0.125rem;
    --sf-input-error-padding-y: 0.125rem;
    --sf-input-error-font-size: 0.75rem;
    --sf-input-error-color: var(--sf-status-color-danger);
    --sf-input-error-background-color: var(--sf-status-background-danger);
    --sf-input-error-box-shadow: 0 20px 25px -5px var(--sf-secondary-box-shadow-color), 0 8px 10px -6px var(--sf-secondary-box-shadow-color);
    --sf-input-scroll-margin-top: 128px;
    --sf-input-scroll-margin-bottom: 128px;
    --sf-input-error-scroll-margin-top: 32px;
    --sf-input-error-scroll-margin-bottom: 32px;

    --sf-textarea-min-height: 150px;
    --sf-textarea-max-height: 400px;

    --sf-checkbox-label-color: rgb(0, 0, 0);
    --sf-checkbox-label-available-color: var(--color-secondary, #585858);
    --sf-checkbox-label-available-font-size: var(--sf-checkbox-label-font-size, 14px);
    --sf-checkbox-label-text-transform: none;
    --sf-checkbox-label-font-size: 0.875rem;
    --sf-checkbox-line-height: 1.125rem;
    --sf-checkbox-checkmark-background-color: rgb(0, 0, 0);
    --sf-checkbox-checkmark-border-color: rgb(0, 0, 0);
    --sf-checkbox-checkmark-border-radius: 0;
    --sf-checkbox-checkmark-check-border-radius: 0;

    --sf-radio-label-color: rgb(0, 0, 0);
    --sf-radio-label-available-color: #585858;
    --sf-radio-label-text-transform: none;
    --sf-radio-label-font-size: 0.875rem;
    --sf-radio-line-height: 1.125rem;
    --sf-radio-checkmark-background-color: rgb(0, 0, 0);
    --sf-radio-checkmark-border-color: rgb(0, 0, 0);

    --sf-switch-font-size: 1rem;
    --sf-switch-line-height: 22px;
    --sf-switch-background-color: rgb(215 215 215);
    --sf-switch-background-color-active: var(--primary-color);
    --sf-switch-background-color-checked: var(--primary-color);
    --sf-switch-btn-background-color: rgb(255 255 255);
    --sf-switch-btn-background-color-checked: rgb(255 255 255);
    --sf-switch-label-text-transform: none;
    --sf-switch-label-color: rgb(0 0 0);
    --sf-switch-label-weight: var(--font-weight-regular);
    --sf-switch-margin-x: 0;
    --sf-switch-margin-y: 0.375rem;

    --sf-select-checked-background: rgb(255 255 255);
    --sf-select-checked-font-weight: var(--font-weight-semi);

    --sf-input-number-min-width: 140px;
    --sf-input-number-min-width-sm: 110px;

    --sf-input-number-error-padding-x: 0;
    --sf-input-number-error-padding-y: 0.25rem;

    --sf-form-stars-size: 1.25rem;
  }
</style>
<style scoped lang="scss">
  .control-wrapper {
    position: relative;
    scroll-margin-top: var(--sf-input-scroll-margin-top);
    scroll-margin-bottom: var(--sf-input-scroll-margin-bottom);
    min-width: 0;
    width: 100%;

    &.control--required {
      :deep() {
        .label, .checkbox-label, .switch-label {
          &:after {
            content: "*";
            color: var(--sf-input-label-required-color);
          }
        }
      }
    }

    &.control--floating {
      .label {
        position: absolute;
        top: 0;
        left: 0;
        font-size: var(--sf-input-label-font-size);
        padding-left: var(--sf-input-label-padding-left);
        margin: 0;
        padding-top: var(--sf-input-label-padding-top);
        transform-origin: 0 0;
        transition-timing-function: var(--sf-input-transition-timing-func);
        transition-property: opacity, font-size, top, padding-top;
        transition-duration: var(--sf-input-transition-duration);
        pointer-events: none;
      }

      :deep(.control) {
        padding-top: var(--sf-input-floating-padding-top);
        padding-bottom: var(--sf-input-floating-padding-bottom);

        &:-webkit-autofill {
          -webkit-box-shadow: 0 0 0px 1000px #fff inset;
        }

        &:focus ~ .label,
        &:not(:placeholder-shown) ~ .label,
        &:-webkit-autofill ~ .label,
        &.select ~ .label {
          opacity: var(--sf-input-label-floating-opacity);
          font-size: var(--sf-input-label-floating-font-size);
          top: var(--sf-input-label-floating-top);
          padding-top: var(--sf-input-label-floating-padding-top);
        }
      }
    }

    &.control--error {
      scroll-margin-top: var(--sf-input-error-scroll-margin-top);
      scroll-margin-bottom: var(--sf-input-error-scroll-margin-bottom);

      :deep() {
        .label, .checkbox-label, .switch-label {
          color: var(--sf-input-error-color);
        }

        select, input, textarea {
          border-color: var(--sf-input-error-color);
          background: var(--sf-input-error-background-color);
        }
      }
    }

    :deep() {
      select, input, textarea {
        &:disabled {
          color: var(--sf-input-color-disabled);
          background-color: var(--sf-input-background-disabled);
          border-color: var(--sf-input-border-disabled);
          cursor: not-allowed;
          pointer-events: none;
        }

        &:focus-visible {
          background-color: var(--sf-input-background-focus);
          border-color: var(--sf-input-border-focus);
          outline: 2px solid var(--sf-input-border-focus);
          outline-offset: 2px;
        }

        &:active {
          background-color: var(--sf-input-background-active);
          border-color: var(--sf-input-border-active);
        }
      }
    }

    :deep(.control) {
      width: 100%;
      color: var(--sf-input-color);
      font-size: var(--sf-input-font-size);
      font-family: var(--sf-input-font-family);
      font-weight: var(--sf-input-font-weight);
      line-height: var(--sf-input-line-height);
      padding: var(--sf-input-padding-top) var(--sf-input-padding-right) var(--sf-input-padding-bottom) var(--sf-input-padding-left);
      background-color: var(--sf-input-background);
      box-shadow: var(--sf-input-box-shadow);

      border-style: var(--sf-input-border-style);
      border-color: var(--sf-input-border);
      border-bottom-width: var(--sf-input-border-bottom-width);
      border-top-width: var(--sf-input-border-top-width);
      border-right-width: var(--sf-input-border-right-width);
      border-left-width: var(--sf-input-border-left-width);
      border-radius: var(--sf-input-border-radius);

      transition-property: var(--sf-input-transition-property);
      transition-timing-function: var(--sf-input-transition-timing-func);
      transition-duration: var(--sf-input-transition-duration);

      &::-webkit-input-placeholder {
        color: transparent;
      }

      &:-moz-placeholder {
        color: transparent;
      }

      &::-moz-placeholder {
        color: transparent;
      }

      &:-ms-input-placeholder {
        color: transparent;
      }
    }
  }

  .label {
    display: inline-block;
    color: var(--sf-input-label-color);
    font-size: var(--sf-input-label-font-size);
    padding-left: var(--sf-input-border-radius);
    padding-bottom: var(--sf-input-label-padding-bottom);
    line-height: var(--sf-input-label-line-height);
    border-right-width: var(--sf-input-border-right-width);
    border-left-width: var(--sf-input-border-left-width);
    border-top-width: 0;
    border-bottom-width: 0;
    border-style: solid;
    border-color: transparent;
  }

  .control-container {
    position: relative;
  }

  .errors {
    padding-top: 0.125rem;

    .error {
      display: flex;
      align-items: flex-start;
      gap: 0.25rem;
      color: var(--sf-input-error-color);
      font-size: 0.75rem;
      min-width: 0;
      width: 100%;

      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        height: 1.5rem;
        width: 1.5rem;
      }

      .message {
        padding: 0.25rem 0;
        line-height: 1rem;
        text-wrap: balance;
      }
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity var(--transition-primary-duration) var(--transition-primary-timing-function);
  }

  .fade-enter-from, .fade-leave-to {
    opacity: 0;
  }
</style>
